import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

import BatchesERP from '../components/OwnerTabs/BatchesERP';
import HomeworkBuilder from '../components/OwnerTabs/HomeworkBuilder';
import CRMUsers from '../components/OwnerTabs/CRMandUsers';
import AcademyHistory from '../components/OwnerTabs/AcademyHistory';
import LiveMonitor from '../components/OwnerTabs/LiveMonitor';
import ProfileModals from '../components/OwnerTabs/ProfileModals';
import ProfileSettings from '../components/ProfileSettings';
import FinanceTracker from '../components/OwnerTabs/FinanceTracker'; 
import CoachDashboard from './CoachDashboard';
import SkeletonLoader from '../components/SkeletonLoader';
import { CartoonAvatar } from '../components/GamificationAssets';

export default function OwnerDashboard({ user }) {
    const [myProfile, setMyProfile] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [allBatches, setAllBatches] = useState([]);
    const [allClasses, setAllClasses] = useState([]);
    const [allHomeworks, setAllHomeworks] = useState([]);

    const [ownerTab, setOwnerTab] = useState('monitor'); 
    const [selectedProfile, setSelectedProfile] = useState(null);

    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();

        const unsubUser = onSnapshot(doc(db, "users", safeEmail), (docSnap) => {
            if (docSnap.exists()) setMyProfile({ id: docSnap.id, ...docSnap.data() });
        });
        const unsubUsers = onSnapshot(collection(db, "users"), (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllUsers(list);
        });
        const unsubBatches = onSnapshot(collection(db, "batches"), (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllBatches(list);
        });
        const unsubClasses = onSnapshot(collection(db, "classes"), (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllClasses(list);
        });
        const unsubHW = onSnapshot(collection(db, "homeworks"), (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllHomeworks(list);
        });

        return () => { unsubUser(); unsubUsers(); unsubBatches(); unsubClasses(); unsubHW(); };
    }, [user]);

    const changeStatus = async (classId, newStatus) => {
        const loadingId = toast.loading("Starting Live Class...");
        try {
            await updateDoc(doc(db, "classes", classId), { status: newStatus });
            toast.success(`Class is now ${newStatus}! Redirecting...`, { id: loadingId });
        } catch (error) {
            toast.error("Failed to start class.", { id: loadingId });
        }
    };

    if (!myProfile) return <SkeletonLoader />;

    const coachesList = allUsers.filter(u => u.role === 'COACH');
    const isOwner = myProfile.role === 'OWNER';

    const TAB_ICONS = {
        monitor: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" fill="currentColor"/>
                <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.83a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/>
            </svg>
        ),
        teach: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <path d="M12 6v6m-3-3h6"/>
            </svg>
        ),
        batches: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                <path d="M6 12v5c3 3 9 3 12 0v-5"/>
            </svg>
        ),
        finance: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="3"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
        ),
        hw: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
        ),
        crm: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
        ),
        history: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
            </svg>
        ),
        settings: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
        )
    };

    const renderOwnerTabBtn = (tabId, label, colorTheme = 'blue') => {
        const isActive = ownerTab === tabId;
        
        let activeGradient = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
        let activeGlow = '0 6px 20px rgba(59, 130, 246, 0.4)';
        let accentColor = '#3b82f6';

        if (colorTheme === 'red') {
            activeGradient = 'linear-gradient(135deg, #ef4444 0%, #be123c 100%)';
            activeGlow = '0 6px 20px rgba(239, 68, 68, 0.4)';
            accentColor = '#ef4444';
        } else if (colorTheme === 'green') {
            activeGradient = 'linear-gradient(135deg, #10b981 0%, #047857 100%)';
            activeGlow = '0 6px 20px rgba(16, 185, 129, 0.4)';
            accentColor = '#10b981';
        } else if (colorTheme === 'amber') {
            activeGradient = 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)';
            activeGlow = '0 6px 20px rgba(245, 158, 11, 0.4)';
            accentColor = '#f59e0b';
        } else if (colorTheme === 'purple') {
            activeGradient = 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)';
            activeGlow = '0 6px 20px rgba(139, 92, 246, 0.4)';
            accentColor = '#8b5cf6';
        } else if (colorTheme === 'gray') {
            activeGradient = 'linear-gradient(135deg, #64748b 0%, #334155 100%)';
            activeGlow = '0 6px 20px rgba(100, 116, 139, 0.35)';
            accentColor = '#64748b';
        }

        return (
            <button 
                key={tabId}
                onClick={() => setOwnerTab(tabId)} 
                style={{
                    padding: '8px 18px', 
                    border: 'none', 
                    borderRadius: '14px', 
                    fontWeight: '700', 
                    cursor: 'pointer', 
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', 
                    fontSize: '13px', 
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '9px',
                    color: isActive ? '#ffffff' : 'var(--text-secondary)',
                    background: isActive ? activeGradient : 'transparent',
                    boxShadow: isActive ? `${activeGlow}, inset 0 1px 0 rgba(255, 255, 255, 0.35)` : 'none',
                    fontFamily: "'Outfit', 'Inter', sans-serif"
                }}
                onMouseOver={e => {
                    if (!isActive) {
                        e.currentTarget.style.background = 'rgba(163, 177, 198, 0.15)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                        e.currentTarget.style.transform = 'translateY(-1.5px)';
                    }
                }}
                onMouseOut={e => {
                    if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }
                }}
            >
                <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: isActive ? '#ffffff' : accentColor,
                    transition: 'color 0.2s ease'
                }}>
                    {TAB_ICONS[tabId]}
                </span>
                <span>{label}</span>
            </button>
        );
    };

    return (
        <div style={{width: '100%', maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px', boxSizing: 'border-box', padding: '10px' }}>
            <ProfileModals selectedProfile={selectedProfile} setSelectedProfile={setSelectedProfile} completedClasses={allClasses.filter(c => c.status === 'COMPLETED')} allHomeworks={allHomeworks} allClasses={allClasses} />

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-primary)', padding: '20px', borderRadius: '16px', marginBottom: '24px', boxShadow: 'var(--panel-shadow)', border: '1px solid rgba(163, 177, 198, 0.3)' }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <CartoonAvatar name={myProfile.name || myProfile.email?.split('@')[0] || 'Owner'} size={70} avatarUrl={myProfile?.avatar} />
                    <div>
                        <h2 style={{margin: '0 0 5px 0', color: 'var(--text-primary)' }}>Academy HQ</h2>
                        <div style={{color: isOwner ? '#a855f7' : '#3b82f6', fontSize: '13px', fontWeight: 'bold' }}>Logged in as: {myProfile.name || myProfile.email} ({isOwner ? 'Owner' : 'Admin'})</div>
                    </div>
                </div>
            </div>

            <div style={{
                display: 'flex',
                gap: '4px',
                marginBottom: '28px',
                overflowX: 'auto',
                padding: '6px',
                background: 'var(--bg-primary)',
                borderRadius: '18px',
                border: '1px solid rgba(163, 177, 198, 0.3)',
                boxShadow: 'var(--panel-shadow)',
                alignItems: 'center'
            }}>
                {renderOwnerTabBtn('monitor', 'Live Monitor', 'red')}
                {renderOwnerTabBtn('teach', 'Teach Class', 'blue')}
                {renderOwnerTabBtn('batches', 'Batches ERP', 'green')}
                {isOwner && renderOwnerTabBtn('finance', 'Finances', 'green')}
                {renderOwnerTabBtn('hw', 'Homeworks', 'amber')}
                {renderOwnerTabBtn('crm', 'CRM & Users', 'blue')}
                {renderOwnerTabBtn('history', 'Class History', 'purple')}
                {renderOwnerTabBtn('settings', 'Settings', 'gray')}
            </div>

            <div style={{width: '100%' }}>
                {ownerTab === 'monitor' && <LiveMonitor allClasses={allClasses} />}
                {ownerTab === 'teach' && <CoachDashboard user={user} isEmbedded={true} />}
                {ownerTab === 'batches' && <BatchesERP allBatches={allBatches} coachesList={coachesList} />}
                {ownerTab === 'finance' && isOwner && <FinanceTracker allBatches={allBatches} allUsers={allUsers} />}
                {ownerTab === 'hw' && <HomeworkBuilder hwFolders={allHomeworks} />}
                {ownerTab === 'crm' && <CRMUsers allUsers={allUsers} setSelectedProfile={setSelectedProfile} isOwner={isOwner} />}
                {ownerTab === 'history' && <AcademyHistory allClasses={allClasses} allUsers={allUsers} />}
                {ownerTab === 'settings' && <ProfileSettings myProfile={myProfile} />}
            </div>
        </div>
    );
}
